import { type Candidate, showsReplacement } from "@vidario/domain";
import { Card, HiddenBlock } from "@vidario/ui";
import { type ReactElement } from "react";

import { CLASSES_NOTE, HIDDEN_TEXT } from "../../anonym/copy";
import { useMaskedCandidate } from "../../anonym/useAnonymView";
import { PeriodLabel } from "./PeriodLabel";

/** Career timeline (MVP §4.3) — vertical list of {fromDate, toDate,
 * title, company, description}. Read-only for Phase 4; admin can edit
 * via a future inline-edit pass if needed. */
export function CareerCard({ candidate: raw }: { candidate: Candidate }): ReactElement | null {
  // In the Anonym-Ansicht (#140): companies as classes, names in titles and descriptions replaced (#172).
  const { candidate, hidden } = useMaskedCandidate(raw);
  if (candidate.careerTimeline.length === 0) return null;
  const texts = (c: Candidate): (string | null)[] =>
    c.careerTimeline.flatMap((e) => [e.title, e.description]);
  return (
    <Card>
      <h3 className="m-0 mb-4 text-[15px] font-semibold">Karriere</h3>
      <ol className="relative m-0 list-none pl-5.5">
        <span
          aria-hidden="true"
          className="absolute left-1.5 top-1.5 bottom-1.5 w-0.5 bg-neutral-200"
        />
        {candidate.careerTimeline.map((s, i) => (
          <li key={s.position} className="relative pb-4.5">
            <span
              aria-hidden="true"
              className={`absolute -left-5 top-1 inline-block h-3 w-3 rounded-full border-2 border-primary-500 ${
                i === 0 ? "bg-primary-500" : "bg-neutral-0"
              }`}
            />
            <PeriodLabel from={s.fromDate} to={s.toDate} />
            <div className="mt-0.5 text-sm font-semibold">{s.title}</div>
            <div className="text-[13px] text-text-secondary">{s.company}</div>
            {s.description && (
              <div className="mt-1 text-[13px] leading-relaxed text-text-tertiary">
                {s.description}
              </div>
            )}
            {hidden?.careerDescriptions.includes(s.position) && (
              <HiddenBlock className="mt-1 text-[13px]">{HIDDEN_TEXT}</HiddenBlock>
            )}
          </li>
        ))}
      </ol>
      {hidden && showsReplacement(texts(raw), texts(candidate)) && (
        <p className="m-0 mt-3 text-xs text-text-tertiary">{CLASSES_NOTE}</p>
      )}
    </Card>
  );
}
